import { TextField, Menu, MenuItem } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { getUrl } from "../../../getUrl";
import { InvalidCredentials, AuthServerUnreachable } from "../../../API/auth";
import Button from "../../../Component/Button/Button";
import MessageFlash, {
  MessageLevel,
} from "../../../Component/message-flash/MessageFlash";
import styles from "./LoginForm.module.css";

export type OnLogin = (params: {
  email: string;
  password: string;
}) => Promise<void>;

type LoginPageProps = {
  onLogin: OnLogin;
  error: Error | null;
};

function LoginFlash({
  error,
}: {
  error: Error | null;
  warning?: string;
}): React.ReactNode {
  const { t } = useTranslation();

  const errorMap = new Map<unknown, null | string>([
    [null, null],
    [undefined, null],
    [InvalidCredentials, t("message.Invalid login credentials")],
    [AuthServerUnreachable, t("message.login.no_connection")],
  ]);

  const result = errorMap.get(error?.constructor);
  const msg = result !== undefined ? result : t("message.Unknown login error");

  return <MessageFlash message={msg} level={MessageLevel.Error} />;
}

export function AgromanagerSsoButton({ onClose }: { onClose: () => void }) {
  const [message, setMessage] = useState<string | null>(null);
  const { t } = useTranslation();

  async function handler() {
    onClose();
    const data = await fetch(getUrl("/v1/auth/agromanager/login_url"), {
      method: "GET",
    })
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .catch(() => {
        setMessage(
          "Could not redirect to Agromanager. Please check your connection.",
        );
        return null;
      });

    if (data) window.location.href = data.url;
  }

  return (
    <>
      <MenuItem sx={{ p: 0 }}>
        <button
          type="button"
          onClick={handler}
          className={styles.agromanagerSsoLogo}
        >
          <img
            style={{ height: "1.5em", marginRight: "7px" }}
            src="/agromanager_sso_logo.svg"
            alt="agromanager_sso_logo"
          />
          {t("button.sign_in_agromanager")}
        </button>
      </MenuItem>
      {message && <MessageFlash message={message} level={MessageLevel.Error} />}
    </>
  );
}

export function PartnerLoginButton() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { t } = useTranslation();
  const open = Boolean(anchorEl);

  function handleOpen(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  return (
    <>
      <Button
        fullWidth
        variant="outlined"
        onClick={handleOpen}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? "partner-login-menu" : undefined}
      >
        {t("button.partner_login")}
      </Button>
      <Menu
        id="partner-login-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { style: { width: anchorEl?.offsetWidth } } }}
      >
        <AgromanagerSsoButton onClose={handleClose} />
      </Menu>
    </>
  );
}

export default function LoginForm({
  onLogin,
  error = null,
}: LoginPageProps): React.ReactNode {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { t } = useTranslation();
  const location = useLocation();
  const ssoError = location.state?.error;

  return (
    <div>
      <h1>{t("title.login")}</h1>
      <form
        onSubmit={(ev) => {
          ev.preventDefault();
          onLogin({ email, password });
        }}
      >
        <fieldset className="boxed">
          <div className="vspace">
            <LoginFlash error={error} />
            {ssoError && (
              <MessageFlash message={t(ssoError)} level={MessageLevel.Error} />
            )}
          </div>
          <div className="vspace">
            <TextField
              label={t("label.Email")}
              required={true}
              variant="outlined"
              fullWidth={true}
              value={email}
              type="email"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setEmail(event.target.value)
              }
            />
          </div>
          <div className="vspace">
            <TextField
              label={t("label.Password")}
              fullWidth={true}
              variant="outlined"
              required={true}
              value={password}
              type="password"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setPassword(event.target.value)
              }
            />
          </div>
          <div className="vspace">
            <Link to="/forgot-password">{t("link.Forgot password")}</Link>
          </div>
          <div className="vspace">
            <Button
              data-testid="button.login"
              type="submit"
              size="large"
              variant="contained"
              fullWidth
              disableElevation
            >
              {t("button.login")}
            </Button>
          </div>
          <div className="vspace">
            <Link to="/verify-email">
              <Button
                size="large"
                variant="contained"
                fullWidth
                disableElevation
                color="secondary"
              >
                {t("button.register")}
              </Button>
            </Link>
          </div>
          <div className="vspace">
            <PartnerLoginButton />
          </div>
        </fieldset>
      </form>
    </div>
  );
}
